import React from 'react';

/**
 * Пользовательское соглашение (публичная оферта для пользователей приложения).
 *
 * Текст обязан **дословно совпадать** с `assets/legal/client_terms.md`
 * мобильного приложения: пользователь принимает документ в приложении, а
 * публикуется он здесь — расхождение означает, что человек согласился не с
 * тем, что опубликовано. Редакция и версия документа — во front matter того
 * же файла; здесь дата дублируется в `TermsClientsPage`.
 */
export const ClientTermsContent: React.FC = () => (
  <>
    <h2>Публичная оферта для пользователей мобильного приложения «Qprice»</h2>
    <p>
      г. Астана. Редакция от 25 августа 2026 года.
    </p>
    <p>
      Товарищество с ограниченной ответственностью «Big Brain Group» (БИН 211140024452),
      являющееся владельцем и оператором мобильного приложения «Qprice» (далее — «Приложение»,
      «Платформа»), публикует настоящий документ, адресованный неограниченному кругу
      физических лиц (далее — «Пользователь»), в качестве публичной оферты в соответствии со
      статьёй 395 Гражданского кодекса Республики Казахстан заключить соглашение об
      использовании Приложения (далее — «Соглашение») на изложенных ниже условиях.
    </p>

    <h2>1. ТЕРМИНЫ И ОПРЕДЕЛЕНИЯ</h2>
    <p>
      1.1. «Приложение» / «Qprice» — мобильное приложение, предоставляющее Пользователям
      информацию об актуальных скидках и специальных предложениях Партнёров.
    </p>
    <p>
      1.2. «Пользователь» — физическое лицо, установившее Приложение и прошедшее регистрацию в
      порядке раздела 3 настоящего Соглашения.
    </p>
    <p>
      1.3. «Партнёр» — индивидуальный предприниматель или юридическое лицо (заведение
      общественного питания, услуг, торговли и т.д.), разместившее на Платформе информацию о
      своих скидках на основании отдельного соглашения с Платформой.
    </p>
    <p>
      1.4. «Скидка» — специальное предложение Партнёра, размещённое им самостоятельно в своём
      профиле на Платформе.
    </p>
    <p>
      1.5. «Отзыв» — текстовая оценка и/или рейтинг, оставленные Пользователем в отношении
      Партнёра через функционал Приложения.
    </p>
    <p>
      1.6. «Корзина» / «Заявка» — функция Приложения, позволяющая Пользователю сформировать
      перечень интересующих его товаров/услуг Партнёра и направить сформированную информацию
      Партнёру в порядке раздела 4 настоящего Соглашения.
    </p>

    <h2>2. ПОРЯДОК АКЦЕПТА СОГЛАШЕНИЯ</h2>
    <p>
      2.1. Регистрация в Приложении осуществляется одним из доступных способов: через
      мессенджер WhatsApp, по номеру мобильного телефона Пользователя посредством одноразового
      SMS-кода подтверждения, либо по адресу электронной почты Пользователя. Конкретные
      способы регистрации, доступные в Приложении, могут дополняться Платформой без изменения
      существа настоящего Соглашения.
    </p>
    <p>
      2.2. При первом запуске Приложения либо при регистрации Пользователю предлагается
      ознакомиться с полным текстом настоящего Соглашения и проставить отметку («галочку») в
      поле «Я принимаю условия Пользовательского соглашения».
    </p>
    <p>
      2.3. Проставление отметки, указанной в п. 2.2, и/или дальнейшее использование
      функционала Приложения после ознакомления с настоящим Соглашением является полным и
      безоговорочным акцептом Соглашения.
    </p>
    <p>
      2.4. Лица, не достигшие 18 (восемнадцати) лет, не вправе использовать функционал
      Приложения, связанный со скидками Партнёров, реализующих алкогольную продукцию и иные
      товары/услуги, оборот которых ограничен для несовершеннолетних.
    </p>

    <h2>3. ПРЕДМЕТ СОГЛАШЕНИЯ</h2>
    <p>
      3.1. Платформа предоставляет Пользователю простой, неисключительный, безвозмездный
      доступ к функционалу Приложения для поиска и просмотра актуальных скидок Партнёров,
      сохранения избранных Партнёров, получения push-уведомлений, оставления Отзывов и
      формирования Заявок (Корзины) в порядке раздела 4 настоящего Соглашения.
    </p>
    <p>
      3.2. Использование Приложения Пользователем является безвозмездным. Платформа вправе
      размещать в Приложении рекламные материалы и баннеры (в том числе рекламу Партнёров и
      третьих лиц) в порядке раздела 8 настоящего Соглашения.
    </p>
    <p>
      3.3. Платформа не является продавцом товаров и не является исполнителем услуг,
      предлагаемых Партнёрами, и не выступает стороной каких-либо сделок между Пользователем и
      Партнёром.
    </p>

    <h2>4. ФУНКЦИЯ БРОНИРОВАНИЯ (КОРЗИНА)</h2>
    <p>
      4.1. Пользователь вправе сформировать в Приложении Заявку (Корзину) с перечнем
      интересующих его товаров/услуг Партнёра. При подтверждении Заявки (нажатии кнопки
      «Оформить» либо аналогичной) сформированная информация направляется Партнёру посредством
      мессенджера WhatsApp.
    </p>
    <p>
      4.2. Платформа не осуществляет приём, обработку или перевод каких-либо денежных средств
      в связи с Заявками из Корзины. Функция Корзины ограничивается исключительно технической
      передачей сформированной Пользователем информации Партнёру и не является бронированием,
      резервированием товара/услуги или заключением сделки между Пользователем и Партнёром.
    </p>
    <p>
      4.3. Отправка Заявки не является подтверждённой бронью. Платформа не гарантирует и не
      контролирует получение, прочтение либо какую-либо обработку Партнёром направленной
      Заявки. Перед отправкой и/или сразу после неё Приложение отображает Пользователю
      уведомление о том, что заявка передаётся Партнёру и не является подтверждённой бронью, с
      рекомендацией дождаться ответа Партнёра либо связаться с ним напрямую для подтверждения.
    </p>
    <p>
      4.4. Дальнейшее оформление брони, заказа или оказание услуги, включая согласование
      деталей, сроков, цены и оплаты, осуществляется Пользователем непосредственно с
      Партнёром, без участия Платформы.
    </p>
    <p>
      4.5. Платформа не несёт ответственности за отказ Партнёра от исполнения Заявки,
      изменение Партнёром условий, указанных в Заявке, либо отсутствие ответа Партнёра на
      Заявку Пользователя.
    </p>

    <h2>5. ИСПОЛЬЗОВАНИЕ СКИДОК</h2>
    <p>
      5.1. Достоверность, актуальность и фактическое предоставление заявленной Скидки является
      исключительной ответственностью соответствующего Партнёра. Платформа не гарантирует и не
      несёт ответственности за то, что Партнёр фактически предоставит Пользователю Скидку на
      условиях, отображённых в Приложении.
    </p>
    <p>
      5.2. В случае отказа Партнёра предоставить заявленную Скидку либо предоставления её на
      иных условиях, Пользователь вправе обратиться в службу поддержки Платформы для фиксации
      жалобы; урегулирование претензии по существу (включая возможную компенсацию)
      осуществляется непосредственно Партнёром.
    </p>
    <p>
      5.3. Платформа вправе на основании обоснованных жалоб Пользователей ограничивать
      отображение недобросовестных Партнёров в Приложении.
    </p>

    <h2>6. ОТЗЫВЫ И РЕЙТИНГИ</h2>
    <p>
      6.1. Пользователь вправе оставлять Отзывы о Партнёрах, с которыми он имел
      непосредственное взаимодействие.
    </p>
    <p>
      6.2. Пользователь гарантирует, что размещаемые им Отзывы являются достоверными, основаны
      на его личном опыте, не содержат оскорблений, клеветы, ненормативной лексики,
      персональных данных третьих лиц и не нарушают прав третьих лиц.
    </p>
    <p>
      6.3. Платформа вправе, но не обязана, модерировать, редактировать или удалять Отзывы, не
      соответствующие требованиям п. 5.2, без предварительного уведомления Пользователя.
    </p>
    <p>
      6.4. Размещая Отзыв, Пользователь предоставляет Платформе безвозмездную неисключительную
      лицензию на его использование, воспроизведение и публичный показ в рамках функционала
      Приложения на весь срок действия настоящего Соглашения.
    </p>
    <p>
      6.5. Платформа не несёт ответственности за содержание Отзывов, размещённых
      Пользователями, и не проверяет их достоверность в обязательном порядке.
    </p>
    <p>
      6.6. Пользователь вправе подать жалобу на контент, размещённый Партнёром (карточка
      заведения, фото, описание, скидки и иные материалы), с использованием функции «Жалоба» в
      Приложении, — в частности, если такой контент является недостоверным, оскорбительным
      либо нарушающим права третьих лиц или законодательство Республики Казахстан. Жалоба на
      контент Партнёра не является Отзывом и рассматривается Платформой отдельно; Партнёр
      самостоятельно несёт ответственность за размещаемый им контент.
    </p>

    <h2>7. ПЕРСОНАЛЬНЫЕ ДАННЫЕ И ГЕОЛОКАЦИЯ</h2>
    <p>
      7.1. Платформа обрабатывает персональные данные Пользователя в соответствии с Законом
      Республики Казахстан «О персональных данных и их защите» и Политикой конфиденциальности,
      размещённой в Приложении.
    </p>
    <p>
      7.2. Доступ к геолокации Пользователя запрашивается отдельным системным разрешением
      устройства и используется исключительно для отображения ближайших Партнёров.
      Пользователь вправе отозвать такое разрешение в любое время через настройки своего
      устройства, что может ограничить часть функционала Приложения.
    </p>
    <p>
      7.3. Принимая настоящее Соглашение, Пользователь соглашается на получение
      push-уведомлений от Приложения об актуальных скидках; Пользователь вправе отключить
      уведомления в настройках Приложения или устройства в любое время.
    </p>

    <h2>8. РЕКЛАМНЫЕ МАТЕРИАЛЫ</h2>
    <p>
      8.1. Платформа вправе размещать в Приложении рекламные баннеры и иные рекламные
      материалы, в том числе на платной основе для Партнёров и третьих лиц-рекламодателей.
    </p>
    <p>
      8.2. Размещение рекламных материалов осуществляется Платформой в соответствии с
      законодательством Республики Казахстан о рекламе.
    </p>
    <p>
      8.3. Использование Приложения Пользователем на условиях настоящего Соглашения не
      предполагает оплаты Пользователем ни в каком виде — источником дохода Платформы являются
      абонентская плата, уплачиваемая Партнёрами за размещение на Платформе, а также доходы от
      размещения рекламных материалов Партнёров и третьих лиц-рекламодателей, а не платежи
      Пользователей.
    </p>

    <h2>9. ИНТЕЛЛЕКТУАЛЬНАЯ СОБСТВЕННОСТЬ</h2>
    <p>
      9.1. Все права на Приложение, его программный код, дизайн, товарный знак «Qprice» и иные
      объекты интеллектуальной собственности принадлежат Платформе.
    </p>
    <p>
      9.2. Настоящее Соглашение не передаёт Пользователю каких-либо прав на объекты
      интеллектуальной собственности Платформы, кроме права на использование функционала
      Приложения по его прямому назначению.
    </p>

    <h2>10. ОТВЕТСТВЕННОСТЬ И ОГРАНИЧЕНИЕ ОТВЕТСТВЕННОСТИ</h2>
    <p>
      10.1. Платформа не несёт ответственности за качество товаров и услуг Партнёров, за
      фактическое предоставление Скидок, за обработку либо необработку Партнёром направленных
      ему Заявок из Корзины, а также за любые убытки Пользователя, возникшие в результате его
      взаимодействия с Партнёром.
    </p>
    <p>
      10.2. Платформа не гарантирует бесперебойную и безошибочную работу Приложения и вправе
      проводить технические работы, приостанавливающие доступ к функционалу.
    </p>
    <p>
      10.3. Пользователь обязуется не использовать Приложение в противоправных целях, не
      предпринимать попыток несанкционированного доступа к системам Платформы.
    </p>

    <h2>11. СРОК ДЕЙСТВИЯ И ПРЕКРАЩЕНИЕ</h2>
    <p>
      11.1. Соглашение действует с момента акцепта и до момента удаления Пользователем своего
      аккаунта либо прекращения использования Приложения.
    </p>
    <p>
      11.2. Пользователь вправе удалить свой аккаунт в любое время через настройки Приложения
      либо обратившись в службу поддержки Платформы.
    </p>
    <p>
      11.3. Платформа вправе заблокировать аккаунт Пользователя при нарушении условий
      настоящего Соглашения (в том числе при систематическом размещении недостоверных
      Отзывов).
    </p>
    <p>
      11.4. Платформа вправе в одностороннем порядке изменять условия настоящего Соглашения,
      публикуя новую редакцию в Приложении. Продолжение использования Приложения после
      вступления изменений в силу считается согласием Пользователя с новой редакцией.
    </p>

    <h2>12. ЗАКЛЮЧИТЕЛЬНЫЕ ПОЛОЖЕНИЯ</h2>
    <p>
      12.1. Настоящее Соглашение регулируется и толкуется в соответствии с законодательством
      Республики Казахстан.
    </p>
    <p>
      12.2. Все споры разрешаются путём переговоров, а при недостижении согласия — в судебном
      порядке по месту нахождения Платформы.
    </p>
    <p>
      12.3. Если какое-либо положение настоящего Соглашения будет признано недействительным,
      это не влечёт недействительности остальных положений.
    </p>

    <h2>Реквизиты Платформы</h2>
    <p>
      ТОО «Big Brain Group», БИН 211140024452
    </p>

    <h2>Приложение № 1. ПЕРЕЧЕНЬ ДАННЫХ, ОБРАБАТЫВАЕМЫХ ПРИ ИСПОЛЬЗОВАНИИ ПРИЛОЖЕНИЯ</h2>
    <p>
      к Пользовательскому соглашению (Публичной оферте для пользователей приложения «Qprice»)
    </p>
    <ul>
      <li>
        Контактные данные для входа — Номер телефона (WhatsApp/SMS) и/или адрес электронной
        почты — в зависимости от выбранного Пользователем способа регистрации; используются
        как логин;
      </li>
      <li>
        Геолокация — Для отображения ближайших Партнёров и актуальных скидок поблизости
        (запрашивается отдельным системным разрешением, может быть отклонено Пользователем);
      </li>
      <li>
        Отзывы и оценки — Текст отзыва, оценка, дата и время публикации, привязка к аккаунту
        Пользователя;
      </li>
      <li>
        Содержимое Заявки (Корзины) — Перечень выбранных товаров/услуг Партнёра, направляемый
        Партнёру в WhatsApp при оформлении Заявки;
      </li>
      <li>
        Избранное и история — Список сохранённых Партнёров, история просмотров скидок;
      </li>
      <li>
        Push-токен устройства — Для отправки уведомлений об актуальных скидках (при согласии
        Пользователя);
      </li>
      <li>
        Технические данные — Тип устройства, версия ОС, версия приложения — для устранения
        ошибок и аналитики.
      </li>
    </ul>
    <p>
      Примечание: Платформа не запрашивает и не хранит данные банковских карт Пользователя —
      использование Приложения безвозмездно для Пользователя и не требует привязки платёжного
      средства.
    </p>
  </>
);
